import { z } from "zod";

// Mirrors RoutingRuleResponse on the wire. EntityType is nullable
// (rule scoped to a kind only) and predicates are an open dictionary
// keyed by RoutingTagKeys; the backend rejects unknown keys at the
// request boundary, so we don't redeclare an enum on the frontend.
//
// First-hop ingest is single-destination by design: a rule names ONE
// target queue. User-driven fan-out across many queues is a separate
// operation (broadcast / attach), not something authored as a routing
// rule with multiple targets.
export const RoutingRuleSchema = z.object({
    id: z.string(),
    workflowKind: z.string(),
    entityType: z.string().nullable().optional(),
    // Optional task-type scope. When non-null the rule routes only task
    // work items whose TaskType matches this value (non-task items
    // carry null and so never satisfy a task-scoped rule). Null is the
    // wildcard and the back-compat default for rules authored before
    // this field existed.
    taskType: z.string().nullable().optional(),
    tagPredicates: z.record(z.string(), z.string()),
    targetQueueId: z.string(),
    description: z.string(),
    isEnabled: z.boolean(),
    createdAt: z.string(),
    createdBy: z.string(),
    updatedAt: z.string().nullable().optional(),
    updatedBy: z.string().nullable().optional(),
});

export type RoutingRule = z.infer<typeof RoutingRuleSchema>;

export const RoutingRuleListResponseSchema = z.object({
    items: z.array(RoutingRuleSchema),
});

export type RoutingRuleListResponse = z.infer<typeof RoutingRuleListResponseSchema>;

// Backs the dry-run preview affordance. UsedTriage tells the UI when
// the system fell through to triage instead of matching any rule, so
// the preview can label the result accordingly. PrimaryQueueId is null
// when the work item would be left unrouted (no rules matched and no
// triage queue is configured) — the UI distinguishes that from the
// triage and routed cases via UsedTriage / null respectively.
export const RoutingTestResponseSchema = z.object({
    primaryQueueId: z.string().nullable(),
    matchedRuleIds: z.array(z.string()),
    usedTriage: z.boolean(),
});

export type RoutingTestResponse = z.infer<typeof RoutingTestResponseSchema>;

export const RoutingTagKeysResponseSchema = z.object({
    keys: z.array(z.string()),
});

export type RoutingTagKeysResponse = z.infer<typeof RoutingTagKeysResponseSchema>;
